import { useEffect, useRef, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import {
  ApiError,
  type BoardConfig,
  type EditableTimeEntry,
  type MyCompany,
  type Task,
  type TimesheetEntry,
  api,
} from "../lib/api"
import { useAuth, useSession } from "../lib/auth"
import { DEFAULT_CONFIG, statusOf, typeOf } from "../lib/boardConfig"
import { parseDuration } from "../lib/duration"
import {
  WORKDAY_MINUTES,
  type Period,
  byDay,
  expandRange,
  formatCard,
  isDayKey,
  isWeekend,
} from "../lib/timesheet"
import { Plus, Trash } from "./icons"
import {
  Badge,
  Button,
  ErrorNote,
  Field,
  IconButton,
  Modal,
  MultiSelect,
  SegmentedControl,
  Select,
  TextArea,
  useToast,
} from "./ui"

// The timesheet's pieces: the grid of days, a card per entry, the add/edit
// dialog and the filters dialog. Modelled on YouTrack's Timesheets: the sheet
// is the caller's own time, unless an admin opened a colleague's, and the add
// dialog lets an admin log for a colleague.
//
// Styled after components/calendar.tsx, the other seven-column grid in the
// app, so the two month views read as one family.

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]

/** The most one save may carry; the server's cap, checked here first so the
 *  dialog explains it instead of bouncing off a 400. */
const MAX_RECORDS = 62
const MAX_DAY_MINUTES = 24 * 60

const dayNumber = (day: string) => Number(day.slice(8, 10))
const monthShort = (day: string) =>
  new Date(`${day}T00:00:00.000Z`).toLocaleDateString(undefined, { month: "short", timeZone: "UTC" })

// ---------- grid ----------

export function TimesheetGrid({
  period,
  entries,
  today,
  configs,
  showCompany = false,
  onAdd,
  onEdit,
}: {
  period: Period
  entries: TimesheetEntry[]
  today: string
  /** Board configs by project id, for the preview's status and type names. */
  configs: Map<string, BoardConfig>
  /** Label each card with its company — the sheet spans several. */
  showCompany?: boolean
  onAdd: (day: string) => void
  onEdit: (entry: TimesheetEntry) => void
}) {
  const { days, totals } = byDay(entries)
  const week = period.mode === "week"

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[840px]">
        <div className="grid grid-cols-7 gap-px">
          {WEEKDAYS.map((d) => (
            <div key={d} className="px-2 py-1 text-[11px] font-medium tracking-wide text-faint uppercase">
              {d}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-px overflow-hidden rounded-lg border border-border bg-border">
          {period.days.map((day, index) => {
            const total = totals.get(day) ?? 0
            const inPeriod = period.inPeriod(day)
            const isToday = day === today
            // The month is named on its first day and on the grid's first
            // cell, so padding days from the neighbouring month say which one.
            const label =
              week || index === 0 || dayNumber(day) === 1 ? `${monthShort(day)} ${dayNumber(day)}` : String(dayNumber(day))
            return (
              <div
                key={day}
                className={`group flex flex-col gap-1.5 p-1.5 ${week ? "min-h-80" : "min-h-32"} ${
                  isToday ? "bg-accent-soft" : isWeekend(day) ? "bg-surface-muted" : "bg-surface"
                }`}
              >
                <div className="flex items-center justify-between gap-1 px-0.5">
                  <span
                    className={`font-mono text-[11px] tabular-nums ${
                      isToday ? "font-semibold text-foreground" : inPeriod ? "text-muted" : "text-faint"
                    }`}
                  >
                    {label}
                  </span>
                  {/* Green at the 8h target and above — an over-full day stays
                      green and shows its real total, as YouTrack does. */}
                  <Badge
                    tone={total >= WORKDAY_MINUTES ? "success" : "neutral"}
                    className="font-mono text-[10px] tabular-nums"
                    title={`${formatCard(total)} logged of an ${formatCard(WORKDAY_MINUTES)} day`}
                  >
                    {formatCard(total)} of {formatCard(WORKDAY_MINUTES)}
                  </Badge>
                </div>
                {(days.get(day) ?? []).map((entry) => (
                  <EntryCard
                    key={entry.id}
                    entry={entry}
                    showNote={week}
                    showCompany={showCompany}
                    config={configs.get(entry.task.projectId)}
                    onEdit={onEdit}
                  />
                ))}
                {/* Revealed on hover, but always in the tab order and always
                    shown where there is no hover, so neither a keyboard nor a
                    touch screen loses the way to add a day's time. */}
                <button
                  type="button"
                  onClick={() => onAdd(day)}
                  aria-label={`Add spent time on ${day}`}
                  title="Add spent time"
                  className="flex items-center justify-center rounded border border-dashed border-border-strong py-1 text-faint opacity-0 transition-opacity group-hover:opacity-100 hover:text-foreground focus-visible:opacity-100 focus-visible:outline-2 focus-visible:outline-interactive [@media(hover:none)]:opacity-100"
                >
                  <Plus className="size-3.5" />
                </button>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

// ---------- card ----------

export function EntryCard({
  entry,
  showNote,
  showCompany = false,
  config,
  onEdit,
}: {
  entry: TimesheetEntry
  showNote: boolean
  showCompany?: boolean
  config?: BoardConfig
  onEdit: (entry: TimesheetEntry) => void
}) {
  return (
    // A div with a button role rather than a <button>: the card holds a link,
    // and a link inside a button is invalid and unreachable by keyboard.
    <div
      role="button"
      tabIndex={0}
      onClick={() => onEdit(entry)}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault()
          onEdit(entry)
        }
      }}
      aria-label={`Edit ${formatCard(entry.minutes)} on ${entry.task.key}`}
      className="flex cursor-pointer flex-col gap-1 rounded border border-border bg-surface px-1.5 py-1 text-[11px] transition-colors hover:border-border-strong focus-visible:outline-2 focus-visible:outline-interactive"
    >
      <div className="flex items-center justify-between gap-1.5">
        <TaskKey task={entry.task} config={config ?? DEFAULT_CONFIG} />
        <span className="shrink-0 rounded bg-surface-muted px-1.5 py-0.5 font-mono text-[10px] text-muted tabular-nums">
          {formatCard(entry.minutes)}
        </span>
      </div>
      {showCompany && <span className="truncate text-[10px] text-faint">{entry.task.companyName}</span>}
      {showNote && entry.note && <p className="line-clamp-3 whitespace-pre-line text-muted">{entry.note}</p>}
    </div>
  )
}

/** The key, linked to its task, with a preview on hover. The preview is fixed
 *  to the viewport rather than absolute inside the cell: the grid scrolls
 *  sideways on narrow screens, and that scroll container would clip it. */
function TaskKey({ task, config }: { task: TimesheetEntry["task"]; config: BoardConfig }) {
  const [at, setAt] = useState<{ left: number; top: number } | null>(null)
  const { company } = useSession()
  const { switchCompany } = useAuth()
  const navigate = useNavigate()
  const status = statusOf(config, task.status).name || task.status
  const type = typeOf(config, task.type).name
  const summary = [
    `${task.key} ${task.title}`,
    task.companyName,
    task.projectName,
    type,
    status,
    task.assigneeName ?? "Unassigned",
  ].join(" · ")

  const show = (element: HTMLElement) => {
    const rect = element.getBoundingClientRect()
    setAt({ left: Math.max(8, Math.min(rect.left, window.innerWidth - 272)), top: rect.bottom + 4 })
  }

  return (
    <>
      <Link
        to={`/tasks/${task.id}`}
        title={summary}
        onClick={(event) => {
          event.stopPropagation()
          // A task page reads as the sidebar's company, so a task from another
          // of your companies is opened by switching to it first.
          if (task.companyId === company.id) return
          event.preventDefault()
          void switchCompany(task.companyId).then(() => navigate(`/tasks/${task.id}`))
        }}
        onKeyDown={(event) => event.stopPropagation()}
        onMouseEnter={(event) => show(event.currentTarget)}
        onMouseLeave={() => setAt(null)}
        onFocus={(event) => show(event.currentTarget)}
        onBlur={() => setAt(null)}
        // Grey once the task is done, as YouTrack greys a resolved issue.
        className={`min-w-0 truncate font-mono text-[11px] font-medium hover:underline ${
          task.done ? "text-faint" : "text-interactive"
        }`}
      >
        {task.key}
      </Link>
      {at && (
        <div
          role="tooltip"
          style={{ left: at.left, top: at.top }}
          className="pointer-events-none fixed z-40 flex w-64 flex-col gap-1.5 rounded-md border border-border bg-surface p-2.5 text-xs shadow-lg"
        >
          <span className="font-mono text-[10px] text-faint">{task.key}</span>
          <span className="font-medium text-foreground">{task.title}</span>
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-muted">
            <dt className="text-faint">Company</dt>
            <dd className="truncate">{task.companyName}</dd>
            <dt className="text-faint">Project</dt>
            <dd className="truncate">{task.projectName}</dd>
            <dt className="text-faint">Type</dt>
            <dd className="truncate">{type}</dd>
            <dt className="text-faint">Status</dt>
            <dd className="truncate">{status}</dd>
            <dt className="text-faint">Assignee</dt>
            <dd className="truncate">{task.assigneeName ?? "Unassigned"}</dd>
          </dl>
        </div>
      )}
    </>
  )
}

// ---------- add / edit dialog ----------

type PickedTask = { id: string; key: string; title: string }

type Row = { id: number; date: string; duration: string }

/** A row's minutes, or the reason it has none. Empty is not an error until
 *  Save is pressed — it just keeps Save disabled. */
function rowMinutes(row: Row): { minutes: number } | { error: string | null } {
  if (!row.duration.trim()) return { error: null }
  const minutes = parseDuration(row.duration)
  if (minutes === null) return { error: "Try 1h 30m, 90m or 1.5" }
  if (minutes > MAX_DAY_MINUTES) return { error: "At most 24h in one day" }
  return { minutes }
}

/** The company's active members, for an admin's "Log for" and the sheet's
 *  person picker — null while not asked for or still loading. The roster the
 *  mention picker reads. */
export function useRoster(company: string | null) {
  const [people, setPeople] = useState<{ id: string; name: string }[] | null>(null)
  useEffect(() => {
    setPeople(null)
    if (!company) return
    let live = true
    api
      .get<{ employees: { id: string; name: string }[] }>("/auth/employees/roster", { company })
      .then((res) => live && setPeople(res.employees))
      .catch(() => live && setPeople([]))
    return () => {
      live = false
    }
  }, [company])
  return people
}

export function EntryDialog({
  entry,
  date,
  companies,
  defaultCompanyId,
  defaultForId = "",
  onClose,
  onSaved,
}: {
  /** The entry being edited; absent means adding. A timesheet card, or a
   *  task page's row (lib/timesheet.ts editableEntry). */
  entry?: EditableTimeEntry
  /** The day an add starts on — the cell's, or today's. */
  date: string
  /** Every company you can log time in; with more than one, an add asks which. */
  companies: MyCompany[]
  /** Where an add starts — the company the sidebar is on. */
  defaultCompanyId: string
  /** Whose time an add starts on: "" for your own, or the colleague whose
   *  sheet an admin has open. */
  defaultForId?: string
  onClose: () => void
  /** After a save or delete — and after finding the entry already gone. The
   *  page closes the dialog and reloads the sheet. */
  onSaved: () => void
}) {
  const editing = Boolean(entry)
  const toast = useToast()
  const nextRow = useRef(1)
  const { employee, company: acting } = useSession()

  // Every write acts for this company (x-moderniser-company), so an entry in
  // another of your companies is edited without switching the sidebar, and
  // the API still checks the membership.
  const [companyId, setCompanyId] = useState(entry?.task.companyId ?? defaultCompanyId)
  const asCompany = { company: companyId }
  const [task, setTask] = useState<PickedTask | null>(
    entry ? { id: entry.task.id, key: entry.task.key, title: entry.task.title } : null,
  )
  const [kind, setKind] = useState<"single" | "range">("single")
  const [rows, setRows] = useState<Row[]>([
    { id: 0, date: entry?.date ?? date, duration: entry ? formatCard(entry.minutes) : "" },
  ])
  const [range, setRange] = useState({ from: date, to: date })
  // Hours per working day of a range — 8h unless a part-time allocation says
  // otherwise, as YouTrack let you type it.
  const [eachDay, setEachDay] = useState(formatCard(WORKDAY_MINUTES))
  // Whose time an add logs: "" is your own. Only an admin of the company is
  // offered anyone else, and the API checks it again.
  const [forId, setForId] = useState(defaultForId)
  const isAdmin =
    employee.role === "platform_admin" ||
    (companyId === acting.id ? employee.role === "admin" : companies.find((c) => c.id === companyId)?.role === "admin")
  const people = useRoster(!editing && isAdmin ? companyId : null)
  const [note, setNote] = useState(entry?.note ?? "")
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  // What the form looked like when it opened, for "discard unsaved changes?".
  const snapshot = (value: {
    companyId: string
    task: PickedTask | null
    kind: string
    rows: Row[]
    range: object
    eachDay: string
    forId: string
    note: string
  }) =>
    JSON.stringify({
      companyId: value.companyId,
      task: value.task?.id ?? null,
      kind: value.kind,
      rows: value.rows.map((r) => [r.date, r.duration]),
      range: value.range,
      eachDay: value.eachDay,
      forId: value.forId,
      note: value.note,
    })
  const [initial] = useState(() => snapshot({ companyId, task, kind, rows, range, eachDay, forId, note }))
  const dirty = snapshot({ companyId, task, kind, rows, range, eachDay, forId, note }) !== initial

  function requestClose() {
    if (busy) return
    if (dirty && !window.confirm("Discard unsaved changes?")) return
    onClose()
  }

  // The records a save would send. Ranges are expanded here, weekdays only at
  // the same hours each, so the server validates a single shape.
  const rangeDays = kind === "range" ? expandRange(range.from, range.to) : []
  const parsed = rows.map(rowMinutes)
  const perDay = rowMinutes({ id: -1, date: range.from, duration: eachDay })
  const perDayMinutes = "minutes" in perDay ? perDay.minutes : null
  const perDayError = "error" in perDay ? (perDay.error ?? "Enter the hours for each day") : null
  const records =
    kind === "range"
      ? perDayMinutes === null
        ? []
        : rangeDays.map((day) => ({ date: day, minutes: perDayMinutes }))
      : rows.flatMap((row, index) => {
          const result = parsed[index]
          return result && "minutes" in result && isDayKey(row.date) ? [{ date: row.date, minutes: result.minutes }] : []
        })
  const rangeValid = isDayKey(range.from) && isDayKey(range.to) && range.from <= range.to
  const rangeHint =
    kind !== "range"
      ? null
      : !rangeValid
        ? "Pick a start and an end, in order."
        : rangeDays.length === 0
          ? "This range has no working days, so there is nothing to log."
          : rangeDays.length > MAX_RECORDS
            ? `One save covers at most ${MAX_RECORDS} working days.`
            : perDayMinutes === null
              ? `${rangeDays.length} working ${rangeDays.length === 1 ? "day" : "days"}`
              : `${rangeDays.length} working ${rangeDays.length === 1 ? "day" : "days"}, ${formatCard(rangeDays.length * perDayMinutes)}`
  const valid =
    Boolean(task) &&
    records.length > 0 &&
    records.length <= MAX_RECORDS &&
    (kind === "range" ? rangeValid : records.length === rows.length)

  async function save() {
    if (!task || !valid) return
    setBusy(true)
    setError(null)
    try {
      if (entry) {
        const record = records[0]!
        await api.patch(`/projects/time/${entry.id}`, { minutes: record.minutes, spentOn: record.date, note }, asCompany)
        toast(`Updated ${task.key}: ${formatCard(record.minutes)} on ${record.date}`)
      } else {
        await api.post(
          "/timesheet/entries",
          { taskId: task.id, note: note.trim() || null, records, ...(forId ? { employeeId: forId } : {}) },
          asCompany,
        )
        const total = records.reduce((sum, r) => sum + r.minutes, 0)
        // Someone else's entries never show on your own sheet, so the toast is
        // the clearest sign the save went where it was meant to.
        const forName = forId ? people?.find((p) => p.id === forId)?.name : null
        toast(
          (records.length === 1
            ? `Logged ${formatCard(total)} on ${task.key}`
            : `Logged ${formatCard(total)} on ${task.key} across ${records.length} entries`) +
            (forName ? ` for ${forName}` : ""),
        )
      }
      onSaved()
    } catch (err) {
      // Deleted from the Time Report while this was open: say so and show the
      // sheet as it now is, rather than leaving a form that can never save.
      if (entry && err instanceof ApiError && err.status === 404) {
        toast("This entry no longer exists — it may have been deleted elsewhere", "error")
        onSaved()
        return
      }
      setError(err)
      setBusy(false)
    }
  }

  async function remove() {
    if (!entry || !window.confirm("Delete this entry?")) return
    setBusy(true)
    setError(null)
    try {
      await api.delete(`/projects/time/${entry.id}`, asCompany)
      toast(`Deleted ${formatCard(entry.minutes)} on ${entry.task.key}`)
      onSaved()
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        toast("This entry was already deleted", "error")
        onSaved()
        return
      }
      setError(err)
      setBusy(false)
    }
  }

  const updateRow = (id: number, change: Partial<Row>) =>
    setRows((all) => all.map((row) => (row.id === id ? { ...row, ...change } : row)))

  return (
    <Modal
      title={editing ? "Edit spent time" : "Add spent time"}
      onClose={requestClose}
      footer={
        <div className="flex items-center gap-2">
          <Button onClick={save} disabled={!valid || busy}>
            {busy ? "Saving…" : "Save"}
          </Button>
          <Button variant="ghost" onClick={requestClose} disabled={busy}>
            Cancel
          </Button>
          {editing && (
            <Button variant="danger" className="ml-auto" onClick={remove} disabled={busy}>
              Delete
            </Button>
          )}
        </div>
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault()
          void save()
        }}
      >
        {companies.length > 1 &&
          (editing ? (
            <div className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-foreground">Company</span>
              <span className="text-muted">{entry?.task.companyName}</span>
            </div>
          ) : (
            <Select
              label="Company"
              name="timesheet-company"
              value={companyId}
              onChange={(event) => {
                // A task belongs to one company, so a new company means a new
                // pick — and so do its people.
                setCompanyId(event.target.value)
                setTask(null)
                setForId("")
              }}
            >
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          ))}
        {!editing && isAdmin && (
          <Select
            label="Log for"
            name="timesheet-for"
            value={forId}
            onChange={(event) => setForId(event.target.value)}
          >
            <option value="">Me</option>
            {(people ?? [])
              .filter((p) => p.id !== employee.id)
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
          </Select>
        )}
        <TaskPicker value={task} onChange={setTask} disabled={editing} company={companyId} />

        {!editing && (
          <div className="flex">
            <SegmentedControl
              ariaLabel="Single dates or a date range"
              value={kind}
              onChange={setKind}
              options={[
                { value: "single", label: "Single dates" },
                { value: "range", label: "Date ranges" },
              ]}
            />
          </div>
        )}

        {kind === "single" ? (
          <div className="flex flex-col gap-2">
            {rows.map((row, index) => {
              const result = parsed[index]
              const rowError = result && "error" in result ? result.error : null
              return (
                <div key={row.id} className="flex flex-col gap-1">
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <Field
                        label="Date"
                        type="date"
                        required
                        name={`date-${row.id}`}
                        value={row.date}
                        onChange={(event) => updateRow(row.id, { date: event.target.value })}
                      />
                    </div>
                    <div className="flex-1">
                      <Field
                        label="Spent time"
                        name={`duration-${row.id}`}
                        placeholder="1w 1d 1h 1m"
                        autoFocus={editing ? false : index === 0 && Boolean(task)}
                        value={row.duration}
                        aria-invalid={Boolean(rowError)}
                        onChange={(event) => updateRow(row.id, { duration: event.target.value })}
                      />
                    </div>
                    {rows.length > 1 && (
                      <IconButton
                        type="button"
                        label="Remove this record"
                        className="mb-1.5"
                        onClick={() => setRows((all) => all.filter((r) => r.id !== row.id))}
                      >
                        <Trash className="size-3.5" />
                      </IconButton>
                    )}
                  </div>
                  {rowError && <span className="text-xs text-danger">{rowError}</span>}
                </div>
              )
            })}
            {!editing && rows.length < MAX_RECORDS && (
              <div>
                {/* type="button": inside the form, the default would submit
                    the rows typed so far and close the dialog. */}
                <Button
                  type="button"
                  variant="subtle"
                  size="sm"
                  onClick={() =>
                    setRows((all) => [
                      ...all,
                      { id: nextRow.current++, date: all.at(-1)?.date ?? date, duration: "" },
                    ])
                  }
                >
                  <Plus className="size-3.5" /> Add another record
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Field
                  label="From"
                  type="date"
                  name="range-from"
                  value={range.from}
                  onChange={(event) => setRange((r) => ({ ...r, from: event.target.value }))}
                />
              </div>
              <div className="flex-1">
                <Field
                  label="To"
                  type="date"
                  name="range-to"
                  value={range.to}
                  onChange={(event) => setRange((r) => ({ ...r, to: event.target.value }))}
                />
              </div>
              <div className="w-20">
                <Field
                  label="Each day"
                  name="range-duration"
                  placeholder="8h"
                  value={eachDay}
                  aria-invalid={Boolean(perDayError)}
                  onChange={(event) => setEachDay(event.target.value)}
                />
              </div>
            </div>
            {perDayError && <span className="text-xs text-danger">{perDayError}</span>}
            <span className={`text-xs ${rangeValid && rangeDays.length > 0 && rangeDays.length <= MAX_RECORDS ? "text-muted" : "text-warning"}`}>
              {rangeHint}
            </span>
          </div>
        )}

        <TextArea
          label="Description"
          name="note"
          placeholder="Describe what you did during this time"
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />
        <ErrorNote error={error} />
        {/* Enter in a field saves, as in every other form in the app. */}
        <button type="submit" hidden />
      </form>
    </Modal>
  )
}

/** A searchable task select. Opens on the tasks assigned to you, the ones you
 *  are most likely logging against; typing searches the whole company (its
 *  active projects), capped so each keystroke stays a small request. */
function TaskPicker({
  value,
  onChange,
  disabled,
  company,
}: {
  value: PickedTask | null
  onChange: (task: PickedTask) => void
  disabled: boolean
  /** Whose tasks to search: the dialog's company, not necessarily the sidebar's. */
  company: string
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  const [results, setResults] = useState<Task[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [active, setActive] = useState(0)

  const term = query.trim()
  useEffect(() => {
    if (!open || disabled) return
    let live = true
    // The first list loads at once; a search waits for the typing to pause.
    const timer = setTimeout(
      () => {
        setLoading(true)
        api
          .get<{ tasks: Task[] }>(
            term ? `/projects/tasks?q=${encodeURIComponent(term)}&limit=50` : "/projects/tasks?assigneeId=me",
            { company },
          )
          .then((res) => {
            if (!live) return
            setResults(res.tasks)
            setActive(0)
            setError(null)
          })
          .catch((err) => live && setError(err))
          .finally(() => live && setLoading(false))
      },
      term ? 250 : 0,
    )
    return () => {
      live = false
      clearTimeout(timer)
    }
  }, [term, open, disabled, company])

  // Another company's list is not this one's: drop the old results at once
  // rather than offer them until the new ones land.
  useEffect(() => setResults(null), [company])

  const options = results ?? []
  const pick = (task: Task) => {
    onChange({ id: task.id, key: task.key, title: task.title })
    setOpen(false)
    setQuery("")
  }

  return (
    <div className="relative flex flex-col gap-1.5">
      <label htmlFor="timesheet-task" className="text-sm font-medium text-foreground">
        Task
      </label>
      <input
        id="timesheet-task"
        role="combobox"
        aria-expanded={open}
        aria-controls="timesheet-task-options"
        aria-autocomplete="list"
        autoComplete="off"
        autoFocus={!disabled && !value}
        disabled={disabled}
        placeholder="Search by key or title"
        value={open ? query : value ? `${value.key} ${value.title}` : ""}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onChange={(event) => {
          setQuery(event.target.value)
          setOpen(true)
        }}
        onKeyDown={(event) => {
          if (event.key === "Escape" && open) {
            // Esc closes the list first and the dialog only on a second press.
            // Stopping it here keeps it from reaching the Modal's listener on
            // the document.
            event.stopPropagation()
            setOpen(false)
          } else if (event.key === "ArrowDown") {
            event.preventDefault()
            setOpen(true)
            setActive((i) => Math.min(i + 1, options.length - 1))
          } else if (event.key === "ArrowUp") {
            event.preventDefault()
            setActive((i) => Math.max(i - 1, 0))
          } else if (event.key === "Enter" && open) {
            // Enter picks rather than submitting a form with no task yet.
            event.preventDefault()
            const task = options[active]
            if (task) pick(task)
          }
        }}
        className="rounded-md border border-border bg-surface px-3 py-2 text-sm outline-none placeholder:text-faint focus:border-interactive focus:ring-2 focus:ring-interactive/30 disabled:bg-surface-muted disabled:text-muted"
      />
      {open && !disabled && (
        <div
          id="timesheet-task-options"
          role="listbox"
          className="absolute top-full right-0 left-0 z-30 mt-1 max-h-72 overflow-y-auto rounded-md border border-border bg-surface py-1 shadow-lg"
        >
          <p className="px-3 pt-1.5 pb-1 text-[10px] font-medium tracking-wide text-faint uppercase">
            {term ? "Matching tasks" : "Assigned to me"}
          </p>
          {error ? (
            <p className="px-3 py-1.5 text-sm text-danger">
              {error instanceof Error ? error.message : "Could not load tasks"}
            </p>
          ) : loading && !results ? (
            <p className="px-3 py-1.5 text-sm text-faint">Loading…</p>
          ) : options.length === 0 ? (
            <p className="px-3 py-1.5 text-sm text-faint">
              {term ? "No tasks match" : "Nothing is assigned to you — type to search"}
            </p>
          ) : (
            options.map((task, index) => (
              <button
                key={task.id}
                type="button"
                role="option"
                aria-selected={index === active}
                // mousedown, not click: the input's blur would close the list
                // before a click could land on it.
                onMouseDown={(event) => {
                  event.preventDefault()
                  pick(task)
                }}
                onMouseEnter={() => setActive(index)}
                className={`flex w-full items-baseline gap-2 px-3 py-1.5 text-left text-sm ${
                  index === active ? "bg-surface-muted" : ""
                }`}
              >
                <span className="shrink-0 font-mono text-xs text-faint">{task.key}</span>
                <span className="min-w-0 flex-1 truncate text-foreground">{task.title}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}

// ---------- filters ----------

export function TimesheetFilters({
  projects,
  showCompany = false,
  projectIds,
  q,
  people = null,
  employeeId = "",
  viewingName = null,
  onApply,
  onClose,
}: {
  projects: { id: string; name: string; keyPrefix: string | null; companyName?: string }[]
  /** Prefix each option with its company — the sheet spans several. */
  showCompany?: boolean
  projectIds: string[]
  q: string
  /** An admin's colleagues, for the Person filter; null hides it. */
  people?: { id: string; name: string }[] | null
  /** Whose sheet is open: "" is your own. */
  employeeId?: string
  /** The open colleague's name, for a link to someone off the roster. */
  viewingName?: string | null
  onApply: (filters: { projectIds: string[]; q: string; employeeId: string }) => void
  onClose: () => void
}) {
  const [selected, setSelected] = useState(projectIds)
  const [text, setText] = useState(q)
  const [person, setPerson] = useState(employeeId)
  const apply = () => onApply({ projectIds: selected, q: text.trim(), employeeId: person })
  const names = projects.filter((p) => selected.includes(p.id)).map((p) => p.name)

  return (
    <Modal
      title="Edit filters"
      onClose={onClose}
      footer={
        <div className="flex items-center gap-2">
          <Button onClick={apply}>Save</Button>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="subtle"
            className="ml-auto"
            onClick={() => {
              setSelected([])
              setText("")
              setPerson("")
            }}
          >
            Reset
          </Button>
        </div>
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault()
          apply()
        }}
      >
        {people && (
          <Select
            label="Person"
            name="timesheet-person"
            value={person}
            onChange={(event) => setPerson(event.target.value)}
          >
            <option value="">Me</option>
            {/* A link to someone off the roster still shows as picked; the sheet says why it will not load. */}
            {employeeId && !people.some((p) => p.id === employeeId) && (
              <option value={employeeId}>{viewingName ?? "Colleague"}</option>
            )}
            {people.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Select>
        )}
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-foreground">Projects</span>
          <div className="flex items-center gap-2">
            <MultiSelect
              label="Projects"
              search
              options={projects.map((p) => ({
                value: p.id,
                label: `${showCompany && p.companyName ? `${p.companyName} · ` : ""}${p.keyPrefix ? `${p.keyPrefix} ${p.name}` : p.name}`,
              }))}
              selected={selected}
              onChange={setSelected}
            />
            <span className="min-w-0 truncate text-xs text-muted">{names.length ? names.join(", ") : "All projects"}</span>
          </div>
        </div>
        <Field
          label="Task filter"
          name="q"
          placeholder="Key or title, e.g. CAD-42 or checkout"
          value={text}
          onChange={(event) => setText(event.target.value)}
        />
        <button type="submit" hidden />
      </form>
    </Modal>
  )
}
